import React, { useRef } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import parseNum from "parse-num"
import Highchart from "components/charts/Highchart"
import theme from "styles/theme"
import { splineColors } from "tokens/colors"
import useElementDimensions from "hooks/useElementDimensions"

const ColumnChart = ({
  data,
  stackColumns,
  splineCount,
  stackLabels,
  splineLabels,
  options = { chart: {}, title: {} },
}) => {
  const containerRef = useRef()
  const { width = 500 } = useElementDimensions(containerRef) || {}
  const condensedView = width < 342

  const { categories, series, axes } = prepareData({
    data,
    stackColumns,
    splineCount,
    stackLabels,
    condensedView,
  })

  return (
    <div ref={containerRef}>
      <Highchart
        options={{
          ...options,
          xAxis: {
            categories,
          },
          yAxis: axes,
          series,
          chart: {
            ...options.chart,
            type: "column",
          },
          title: {
            ...options.title,
            margin: 40,
          },
          legend: {
            align: "center",
            itemMarginBottom: 10,
            itemStyle: {
              fontSize: 12,
              fontWeight: 400,
              color: theme.gray,
            },
          },
          tooltip: {
            shared: true,
            backgroundColor: theme.white,
          },
          plotOptions: {
            column: {
              stacking: stackColumns,
            },
            spline: {
              dataLabels: splineLabels
                ? {
                    enabled: true,
                    backgroundColor: theme.lightOrange,
                    borderColor: theme.orange,
                    borderWidth: 2,
                    shadow: true,
                    y: -8,
                    style: {
                      textOutline: "none",
                    },
                  }
                : {},
              marker: {
                lineWidth: 2,
                fillColor: theme.lightOrange,
                lineColor: null,
                states: {
                  hover: {
                    lineWidth: 2,
                  },
                },
              },
            },
          },
        }}
      />
    </div>
  )
}

ColumnChart.propTypes = {
  splineCount: PropTypes.number.isRequired,
  stackColumns: PropTypes.bool.isRequired,
  stackLabels: PropTypes.bool,
  splineLabels: PropTypes.bool,
  data: PropTypes.shape({
    rows: PropTypes.arrayOf(
      PropTypes.shape({ cells: PropTypes.array.isRequired }).isRequired
    ).isRequired,
  }).isRequired,
  options: PropTypes.shape({
    chart: PropTypes.object.isRequired,
  }),
}

export default ColumnChart

const prepareData = ({
  data,
  stackLabels,
  splineCount,
  condensedView = false,
}) => {
  const [{ cells: heading }, ...rows] = data.rows
  const colors = [...splineColors]

  const series = heading.map((name, columnIndex) => {
    const yAxis = Math.max(
      0,
      -1 * (heading.length - splineCount - (columnIndex + 1))
    )

    return {
      name,
      type: yAxis > 0 ? "spline" : "column",
      yAxis,
      data: [],
      color: yAxis > 0 ? colors.shift() : undefined,
      visible: yAxis > 0 ? !condensedView : undefined,
    }
  })

  rows.forEach(({ cells }) => {
    cells.forEach((value, index) => {
      series[index].data.push(index === 0 ? value : parseNum(value))
    })
  })

  return {
    categories: series[0].data,
    series: series.slice(1),

    axes: [
      buildPrimaryAxis({ label: heading[0], stackLabels }),

      splineCount >= 1 &&
        buildSecondaryAxis({
          label: condensedView ? null : heading[heading.length - splineCount],
        }),

      splineCount >= 2 &&
        buildSecondaryAxis({
          label: condensedView ? null : heading[heading.length - 1],
        }),
    ].filter(Boolean),
  }
}

const buildPrimaryAxis = ({ label, stackLabels, ...rest }) => ({
  title: {
    reserveSpace: false,
    text: label,
    align: "high",
    textAlign: "left",
    rotation: 0,
    y: -15,
    x: 0,
    style: {
      whiteSpace: "nowrap",
    },
  },
  stackLabels: {
    enabled: !!stackLabels,
  },
  ...rest,
})

const buildSecondaryAxis = ({ label, ...rest }) => ({
  title: { text: label },
  opposite: true,
  min: 0,
  ...rest,
})

export const query = graphql`
  fragment ColumnChart on SanityColumnChart {
    type: _type
    layout
    title
    subtitle
    stackLabels
    splineLabels
    stackColumns
    splineCount
    data {
      rows {
        cells
      }
    }
    footnotes
  }
`
